import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Modal, Input, notification } from "antd";
import moment from "moment";
import TobNavBar from "../../DashBaord/Top_navbar";
import { changeStatus } from "../../../controllers/V1/pullOutRequest/pullOutRequestController";
import { usePermissions } from "../../../controllers/PermissionContext";
import { formatProjectDate } from "../../../controllers/common";

const { TextArea } = Input;

const STAGE_ACTIONS = [
  { label: "Pull-Out Approve", action: "deployment_approve", color: "rgb(17 174 87)", remarkKey: "deployment_remarks" },
  { label: "Pull-Out Reject",  action: "deployment_reject",  danger: true, remarkKey: "deployment_remarks" },
  { label: "Account Approve",    action: "account_approve",    color: "#1677ff", remarkKey: "account_remarks" },
  { label: "Account Reject",     action: "account_reject",     danger: true, remarkKey: "account_remarks" },
  { label: "BDM Approve",        action: "bdm_approve",        color: "#722ed1", remarkKey: "bdm_remarks" },
  { label: "BDM Reject",         action: "bdm_reject",         danger: true, remarkKey: "bdm_remarks" },
];

export default function PullOutRequestView() {
  const location = useLocation();
  const navigate  = useNavigate();
  const item      = location.state;
  const PERMISSION = usePermissions("userPullOutRequest");

  const [actionModal, setActionModal] = useState({ open: false, action: "", title: "", remarkKey: "" });
  const [remarks, setRemarks]         = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openModal = (action, title, remarkKey) => {
    setRemarks("");
    setActionModal({ open: true, action, title, remarkKey });
  };

  const handleAction = async () => {
    if (!item?._id) return;
    setIsSubmitting(true);
    const res = await changeStatus({
      id: item._id,
      action: actionModal.action,
      [actionModal.remarkKey]: remarks,
      remarks,
    });
    setIsSubmitting(false);
    if (res.status) {
      notification.success({ message: "Status updated successfully" });
      setActionModal({ open: false, action: "", title: "", remarkKey: "" });
      navigate("/request/pull-out-list");
    } else {
      notification.error({ message: res.message || "Failed to update status" });
    }
  };

  const statusDot = (status) => {
    const colors = { Pending: "#f5a623", Approved: "rgb(17 174 87)", Completed: "rgb(17 174 87)", Rejected: "#C51020" };
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: colors[status] || "#888", display: "inline-block" }} />
        <strong>{status}</strong>
      </span>
    );
  };

  const userName = (u) => {
    if (!u) return "—";
    return `${u.users_name || ""} ${u.last_name || ""}`.trim() || u.users_mobile || "—";
  };

  if (!item) {
    return (
      <div className="container-fluid">
        <TobNavBar title="Pull-Out Request View" />
        <p>No data. Go back to list.</p>
      </div>
    );
  }

  const canChangeStatus = PERMISSION.fullAccess === 'Y' || PERMISSION.changeStatus === 'Y';
  const isPending = item.status === 'Pending';

  const availableActions = STAGE_ACTIONS.filter(({ action }) => {
    if (!canChangeStatus || !isPending) return false;
    if (action === 'deployment_approve' || action === 'deployment_reject') return item.deployment_status === 'Pending';
    if (action === 'account_approve' || action === 'account_reject') return item.deployment_status === 'Approved' && item.account_status === 'Pending';
    if (action === 'bdm_approve' || action === 'bdm_reject') return item.deployment_status === 'Approved' && item.account_status === 'Approved' && item.bdm_status === 'Pending';
    return false;
  });

  return (
    <>
      <div className="container-fluid">
        <TobNavBar title="Pull-Out Request View" />

        <div className="card shadow mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Pull-Out Request Details</h5>
            <Button onClick={() => navigate("/request/pull-out-list")}>Back to List</Button>
          </div>
          <div className="card-body">
            {/* User Info */}
            <h6 className="mb-2">User Information</h6>
            <div className="row mb-3">
              <div className="col-md-4 mb-2">
                <label className="all_section_for_label_main_class">User</label>
                <p className="form-control-plaintext">{userName(item.users_id)}</p>
              </div>
              <div className="col-md-4 mb-2">
                <label className="all_section_for_label_main_class">Mobile</label>
                <p className="form-control-plaintext">{item.users_id?.users_mobile || "—"}</p>
              </div>
              <div className="col-md-4 mb-2">
                <label className="all_section_for_label_main_class">POS Number</label>
                <p className="form-control-plaintext">{item.users_id?.pos_number || "—"}</p>
              </div>
              <div className="col-md-4 mb-2">
                <label className="all_section_for_label_main_class">Store</label>
                <p className="form-control-plaintext">{item.users_id?.store_name || "—"}</p>
              </div>
              <div className="col-md-4 mb-2">
                <label className="all_section_for_label_main_class">Area</label>
                <p className="form-control-plaintext">{item.users_id?.area || "—"}</p>
              </div>
              {item.bind_person_id && (
                <div className="col-md-4 mb-2">
                  <label className="all_section_for_label_main_class">Bound Person</label>
                  <p className="form-control-plaintext">{userName(item.bind_person_id)}</p>
                </div>
              )}
            </div>

            {/* Request Details */}
            <h6 className="mb-2">Request Details</h6>
            <div className="row mb-3">
              <div className="col-md-4 mb-2">
                <label className="all_section_for_label_main_class">Contact No.</label>
                <p className="form-control-plaintext">{item.contact_no || "—"}</p>
              </div>
              <div className="col-md-4 mb-2">
                <label className="all_section_for_label_main_class">Collection Received</label>
                <p className="form-control-plaintext">{item.collection_received || "—"}</p>
              </div>
              <div className="col-md-4 mb-2">
                <label className="all_section_for_label_main_class">POS Condition</label>
                <p className="form-control-plaintext">{item.pos_condition || "—"}</p>
              </div>
              <div className="col-md-6 mb-2">
                <label className="all_section_for_label_main_class">Reason</label>
                <p className="form-control-plaintext">{item.reason || "—"}</p>
              </div>
              <div className="col-md-6 mb-2">
                <label className="all_section_for_label_main_class">Remarks</label>
                <p className="form-control-plaintext">{item.remarks || "—"}</p>
              </div>
              <div className="col-md-6 mb-2">
                <label className="all_section_for_label_main_class">Admin Remarks</label>
                <p className="form-control-plaintext">{item.admin_remarks || "—"}</p>
              </div>
              <div className="col-md-6 mb-2">
                <label className="all_section_for_label_main_class">Created</label>
                <p className="form-control-plaintext">{item.createdAt ? moment(formatProjectDate(item.createdAt)).format("DD-MM-YYYY hh:mm A") : "—"}</p>
              </div>
            </div>

            {/* Stage Status */}
            <h6 className="mb-2">Stage Status</h6>
            <div className="row mb-3">
              <div className="col-md-4 mb-2">
                <label>Deployment:</label><br />{statusDot(item.deployment_status)}
                {item.deployment_remarks && <p className="text-muted small mt-1">{item.deployment_remarks}</p>}
              </div>
              <div className="col-md-4 mb-2">
                <label>Account:</label><br />{statusDot(item.account_status)}
                {item.account_remarks && <p className="text-muted small mt-1">{item.account_remarks}</p>}
              </div>
              <div className="col-md-4 mb-2">
                <label>BDM:</label><br />{statusDot(item.bdm_status)}
                {item.bdm_remarks && <p className="text-muted small mt-1">{item.bdm_remarks}</p>}
              </div>
              <div className="col-md-4 mb-2">
                <label>Overall Status:</label><br />{statusDot(item.status)}
              </div>
            </div>

            {/* Actions */}
            {availableActions.length > 0 && (
              <div className="mt-3 d-flex gap-2 flex-wrap">
                {availableActions.map(({ label, action, color, danger, remarkKey }) => (
                  <Button key={action} type="primary" danger={!!danger}
                    style={color && !danger ? { backgroundColor: color } : {}}
                    onClick={() => openModal(action, label, remarkKey)}>
                    {label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        title={actionModal.title}
        open={actionModal.open}
        onCancel={() => setActionModal({ open: false, action: "", title: "", remarkKey: "" })}
        footer={[
          <Button key="cancel" onClick={() => setActionModal({ open: false, action: "", title: "", remarkKey: "" })}>Cancel</Button>,
          <Button key="submit" type="primary" loading={isSubmitting} danger={actionModal.action?.includes("reject")} onClick={handleAction}>Confirm</Button>,
        ]}
      >
        <div className="mb-3">
          <label>Remarks:</label>
          <TextArea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Enter remarks..." />
        </div>
      </Modal>
    </>
  );
}
